import React, {useState, useEffect, useRef, useContext} from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Stack, Box,
} from '@mui/material';
import { purple, green, red } from '@mui/material/colors';
import {getAllEventsSummaryList, getEventAttendees} from "./BrowserDatabaseFunctions";
import {PAContext} from "../../../Services/PAContext";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";


// --- MOCK LanguageModel API ---
const LanguageModel = window.LanguageModel || {
    availability: async () => 'available',
    create: async (options) => ({
        prompt: async (text) => `💡 Prompt response: "${text}"`,
        translate: async (text) => `🌎 Translated text: "${text}"`,
        rewrite: async (text) => `📝 Proofread text: "${text}"`,
        summarize: async (text) => `💡 Summarizer response: "${text}"`,
    }),
};

// Detect desktop
const isDesktop = () => {
    const userAgent = navigator.userAgent;
    return !/Android|iPhone|iPad|iPod|ChromeOS/i.test(userAgent);
};

const NanoDashboard = () => {

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account, authenticated } = dataArray;


    const [availabilityStatus, setAvailabilityStatus] = useState('checking');
    const [loadingStatus, setLoadingStatus] = useState('Checking API status...');
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [loading, setLoading] = useState(false);
    const [isDesktopDevice, setIsDesktopDevice] = useState(false);

    // Track individual API availability
    const [apiAvailable, setApiAvailable] = useState({
        translate: false,
        rewrite: false,
        prompt: false,
        summarize: false
    });

    // Separate histories
    const [userMessages, setUserMessages] = useState([]);
    const [aiMessages, setAiMessages] = useState([]);

    const userEndRef = useRef(null);
    const aiEndRef = useRef(null);

    const scrollToBottom = (ref) => {
        ref.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom(userEndRef);
    }, [userMessages]);

    useEffect(() => {
        scrollToBottom(aiEndRef);
    }, [aiMessages]);


    const [promptSession, setPromptSession] = useState(null);

    useEffect(() => {
        if (!isDesktop()) {
            setIsDesktopDevice(false);
            setLoadingStatus('This API only works on desktop devices.');
            setAvailabilityStatus('unavailable');
            return;
        }
        setIsDesktopDevice(true);

        const checkApis = async () => {
            if (typeof window === 'undefined') return; // Safety check

            // Prompt API
            let promptAvailable = false;
            let isPromptDownloading = false;

            if ('LanguageModel' in window) {
                try {
                    const status = await window.LanguageModel.availability({
                        expectedInputs: [{type: 'text', languages: ['en']}],
                        expectedOutputs: [{type: 'text', languages: ['en']}],
                    });
                    console.log("LanguageModel status : ", status);
                    promptAvailable = status === 'available' || status === 'downloadable' || status === 'downloading';
                    isPromptDownloading = status === 'downloading';

                    console.log("promptAvailable : ", promptAvailable, "isDownloading:", isPromptDownloading);

                    if (promptAvailable) {

                        console.log("creating the Prompt : ");

                        const newPromptSession = await window.LanguageModel.create(
                            {
                                expectedInputs: [
                                    { type: "text", languages: ["en"] }
                                ],
                                expectedOutputs: [
                                    { type: "text", languages: ["en"] }
                                ],
                                temperature: 0.1,
                                topK: 2,
                                initialPrompts: [
                                    {
                                        role: 'system',
                                        content: `You are an expert assistant for Planned Adventure, 
                                            a comprehensive travel and event planning app. 
                                            Your role is to understand and assist users with all aspects of the app, 
                                            which has over a dozen integrated features. Planned Adventure allows users to 
                                            create, manage, and optimize complex travel itineraries and event schedules. 
                                            Key features include multi-destination trip planning, customizable event timelines, 
                                            activity recommendations, budget tracking, collaborative planning with friends or colleagues, 
                                            automated reminders, travel logistics integration (flights, hotels, transportation), 
                                            dynamic route optimization, local attraction suggestions, personalized notifications, 
                                            multi-platform syncing, and AI-powered travel insights. You should be able to provide guidance, 
                                            generate content, answer questions, make suggestions, and simulate workflows for any 
                                            of these features. You should also consider edge cases, such as overlapping events, 
                                            budget constraints, travel restrictions, and user preferences. Your responses should be practical, 
                                            accurate, user-friendly, and aligned with Planned Adventure’s goal of making complex travel 
                                            and event planning simple, efficient, and enjoyable. It's important that you are 
                                            always honest and don't lie or make up things. I don't want you to simulate things. If you need 
                                            to answer questions about events you just use the function calls you need and the browser logic 
                                            will do the work to get the data you request. Today's date is ${new Date().toString()}. Now, 
                                            i'm including a list of tool functions that you can call to get very detailed data about 
                                            the user and all of the users events : 
                                            getAccountId() /* this gets the id of the current user */,
                                            getAccountDetails() /* this gets the entire Account object of the current user */,
                                            sendMeATestEmail() /* make sure they have an email address with their Account; and see if they would like to include any special message to be included in the body of the email */,
                                            getEventPlanningId() /* this gets the ID of the event that is currently loaded into the UI Planner */,
                                            getEventInPlanning() /* this gets the entire Event object that is currently loaded into the UI Planner for viewing and editing */,
                                            getEventSummaryList() /* requires you get the event id from the getEventPlanningId function */,
                                            getAllEventsSummaryList() /* this returns a list of all events for this user; could be in the hundreds */,
                                            getEventAttendees() /* event_id needs to be included for this */.
                                            So to begin with, you need to get up to speed very quickly by calling the getAccountId and getAccountDetails 
                                            to become super helpful right away.
                                            `
                                    },
                                    {
                                        role: 'assistant',
                                        content: `These are tool_calls that we can use to get exact answers to users questions : 
                                            getAccountId, getAccountDetails, sendMeATestEmail, getEventPlanningId, getEventInPlanning,
                                            getEventSummaryList, getAllEventsSummaryList, getEventAttendees.
                                            What we need to do right away is to call the tools getAccountId and getAccountDetails
                                            to become super helpful right away.`
                                    },
                                ],
                            });

                        setPromptSession(newPromptSession);

                        console.log("!!!!!!!!!!!!!! newPromptSession : ", newPromptSession);
                    }

                } catch (e){
                    console.log("!!!!!!!!!!!!!! error : ", e);
                }
            }

            // Translate API
            let translateAvailable = false;
            if ('Translator' in window) {
                try {
                    const status = await window.Translator.availability({
                        sourceLanguage: 'en',
                        targetLanguage: 'de',
                    });
                    console.log("Translator status : ", status);
                    translateAvailable = status === 'available' || status === 'downloadable';
                } catch (e){
                    console.error("error "+e);
                }
            }

            // Rewriter API
            let rewriterAvailable = false;
            if ('Rewriter' in window) {
                try {
                    const status = await window.Rewriter.availability({
                        expectedInputLanguages: ["en"],
                        expectedContextLanguages: ["en"],
                        outputLanguage: "en",
                    });
                    console.log("Rewriter status : ", status);
                    rewriterAvailable = status === 'available' || status === 'downloadable';
                } catch {}
            }

            // Summarizer API
            let summarizerAvailable = false;
            if ('Summarizer' in window) {
                try {
                    const status = await window.Summarizer.availability({
                        expectedInputLanguages: ["en"],
                        expectedContextLanguages: ["en"],
                        outputLanguage: "en",
                    });
                    console.log("Summarizer status : ", status);
                    summarizerAvailable = status === 'available' || status === 'downloadable';
                } catch {}
            }

            setApiAvailable({
                translate: translateAvailable,
                rewrite: rewriterAvailable,
                prompt: promptAvailable,
                summarize: summarizerAvailable
            });

            const allAvailable = promptAvailable || translateAvailable || rewriterAvailable || summarizerAvailable;
            setAvailabilityStatus(allAvailable ? 'available' : 'unavailable');
            setLoadingStatus(allAvailable ? 'APIs ready to use!' : 'No APIs available.');
        };

        checkApis();

    }, []);

    const handleApiCall = async (type) => {
        if (!inputText.trim()) {
            setOutputText('Please enter some text.');
            return;
        }

        if (!navigator.userActivation.isActive) {
            setOutputText('User action required. Please click the button again.');
            return;
        }

        const userMessage = inputText.trim();
        setUserMessages(prev => [...prev, userMessage]);
        setInputText('');
        setLoading(true);

        try {
            let result = '';
            let session;

            // 1️⃣ Get the initial AI result
            switch (type) {
                case 'prompt':
                    result = await promptSession.prompt(userMessage);
                    break;
                case 'translate':
                    session = await window.Translator.create({
                        sourceLanguage: 'en',
                        targetLanguage: 'ge',
                    });
                    result = await session.translate(userMessage);
                    break;
                case 'rewrite':
                    session = await window.Rewriter.create();
                    result = await session.rewrite(userMessage);
                    break;
                case 'summarize':
                    session = await window.Summarizer.create();
                    result = await session.summarize(userMessage);
                    break;
                default:
                    result = 'Invalid API call type.';
            }

            console.log("Initial AI result:", result);

            // 4️⃣ Update the UI
            setAiMessages(prev => [...prev, result.replace(/```[\s\S]*?```/g, '').trim()]);
//            setOutputText(result);

            // 2️⃣ Check for AI-triggered function calls or special blocks
            handleAiFunctionCallCheck(result);

        } catch (error) {
            setLoadingStatus(`API error: ${error.message}`);
            setOutputText('Error processing request: ' + error.message);
            console.error(error);
        } finally {
            setLoading(false);
            setLoadingStatus('API call complete.');
        }
    };

    const handleApiCallOLD = async (type) => {
        if (!inputText.trim()) {
            setOutputText('Please enter some text.');
            return;
        }

        if (!navigator.userActivation.isActive) {
            setOutputText('User action required. Please click the button again.');
            return;
        }

        const userMessage = inputText.trim();
        setUserMessages(prev => [...prev, userMessage]);
        setInputText('');
        setLoading(true);

        try {
            let result = '';
            let session;

            switch (type) {
                case 'prompt':
                    result = await promptSession.prompt(userMessage);
                    setAiMessages(prev => [...prev, result]);
                    break;
                case 'translate':
                    session = await window.Translator.create({
                        sourceLanguage: 'en',
                        targetLanguage: 'fr',
                    });
                    result = await session.translate(userMessage);
                    setAiMessages(prev => [...prev, result]);
                    break;
                case 'rewrite':
                    session = await window.Rewriter.create({
                        sharedContext: "A review for the Flux Capacitor 3000 from TimeMachines Inc."
                    });
                    result = await session.rewrite({
                        context: userMessage
                    });
                    setAiMessages(prev => [...prev, result]);
                    break;
                case 'summarize':
                    session = await window.Summarizer.create();
                    result = await session.summarize(userMessage);
                    setAiMessages(prev => [...prev, result]);
                    break;
                default:
                    result = 'Invalid API call type.';
            }

            console.log("AI results : ", result);

            /*
                see if the result has any :
                function calls,
                ```tool_call,
                ```html,
                ```json
                otherwise just display the output
             */
            handleAiFunctionCallCheck(result);

            setOutputText(result);
        } catch (error) {
            setLoadingStatus(`API error: ${error.message}`);
            setOutputText('Error processing request: ' + error.message);
            console.error(error);
        } finally {
            setLoading(false);
            setLoadingStatus('API call complete.');
        }
    };

    const isApiReady = availabilityStatus === 'available' || availabilityStatus === 'downloadable';

    const aiFunctions = {
        getEventPlanningId: async () => {
            return PA_GlobalObject.Planning.id;
        },
        getAccountId: async () => {
            return Account.id;
        },
        getAccountDetails: async () => {
            return {
                fname: Account.fname,
                lname: Account.lname,
                nickname: Account.nickname,
                address: Account.address,
                city: Account.city,
                state: Account.state,
                zip: Account.zip,
                country: Account.country,
                email: Account.email,
                phone: Account.phone,
                phone_prefix: Account.phone_prefix,
                preferred_language: Account.language,
                preferred_airport: Account.preferred_airport,
                events_count : Account.events.length
            };
        },
        getEventSummaryList: async () => {
            // Wait for the async function to finish
            const _response = await getAllEventsSummaryList(Account.id);
            console.log("_response ---- ", _response);

            if (_response.length > 0){
                return _response;
            } else {
                return 'No events found';
            }
        },
        getAllEventsSummaryList: async () => {
            // Wait for the async function to finish
            const _response = await getAllEventsSummaryList(Account.id);
            console.log("_response ---- ", _response);

            if (_response.length > 0){
                return _response;
            } else {
                return 'No events found';
            }
        },
        getEventSummaryDetails: async (_event_id) => {
            return `Summary details for event ${_event_id}`;
        },
        getEventTimeline: async (_event_id) => {
            return `Timeline for event ${_event_id}`;
        },
        getEventAttendees: async (_event_id) => {
            if (_event_id){
                const _response = await getEventAttendees(_event_id);
                console.log("_response ---- ", _response);

                if (_response?.length > 0){
                    return _response;
                } else {
                    return 'No attendees found';
                }
            } else {
                console.log("sending the FUCKED UP result back to AI");
                setLoading(true);
                // Then prompt the AI again to get the response
                await promptSession.prompt(
                    `you stupid piece of shit!! why would you try to get the 
                    attendee list without providing an event id! try again`
                );
            }

        },
        getEventActivities: async (_event_id) => {
            return `Activities for event ${_event_id}`;
        },
        getEventLodging: async (_event_id) => {
            return `Lodging info for event ${_event_id}`;
        },
        getEventMeals: async (_event_id) => {
            return `Meals info for event ${_event_id}`;
        },
        getEventFlights: async (_event_id) => {
            return `Flights info for event ${_event_id}`;
        },
        getEventSupplies: async (_event_id) => {
            return `Supplies for event ${_event_id}`;
        },
        getEventTodos: async (_event_id) => {
            return `Todos for event ${_event_id}`;
        },
        getEventIdeas: async (_event_id) => {
            return `Ideas for event ${_event_id}`;
        },
        getEventDocuments: async (_event_id) => {
            return `Documents for event ${_event_id}`;
        },
        sendMeATestEmail: async (_include_message) => {
            console.log("sending a test email with a message included : ", _include_message);
            if (authenticated && PA_GlobalObject.Planning.status !== 10){
                const _sendString = {};
                _sendString.action = "E_sendAccountTestEmail";
                _sendString.message = _include_message;
                sendSocketMessage(_sendString);

                return true;
            } else {
                return "the user isn't logged in and they need to be"
            }
        },
    };

    const extractParam = (aiText, fnName) => {
        // Match anything between parentheses for the given function name
        const escapedFnName = fnName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const match = aiText.match(new RegExp(`${escapedFnName}\\(([^)]*)\\)`));

        if (!match) return null;

        let rawParam = match[1].trim();

        // Handle key=value style: eventId="abc123"
        if (rawParam.includes('=')) {
            const parts = rawParam.split('=');
            rawParam = parts[1].replace(/^["']|["']$/g, ''); // remove quotes if any
        }

        // Remove quotes if someone put them around the value
        rawParam = rawParam.replace(/^["']|["']$/g, '');

        return rawParam;
    };


    const handleAiFunctionCallCheck = async (aiText) => {

        console.log("handleAiFunctionCallCheck: See if AI has asked for any tool calls");

        // Check each registered function
        for (const fnName in aiFunctions) {
            if (aiText.includes(fnName)) {

                console.log("Found a function call : ", fnName);


                console.log(extractParam(aiText, fnName));


                // Match functionName(...) optionally with quotes, spaces, or key=value
                const match = aiText.match(new RegExp(`${fnName}\\((.*?)\\)`));
                let param = null;

                if (match) {
                    param = match[1].trim();          // remove extra spaces
                    // Remove possible key= or quotes
                    if (param.includes('=')) {
                        param = param.split('=')[1].replace(/^["']|["']$/g, '');
                    } else {
                        param = param.replace(/^["']|["']$/g, '');
                    }
                }

                console.log("param:", param);

                // Await the async function
                if (fnName === "getAccountId" ) {
                    const functionResult = await aiFunctions[fnName](param);
                    console.log("functionResult : ", functionResult);
                    if (functionResult) {
                        console.log("sending the result back to AI");
                        setLoading(true);
                        setAiMessages(prev => [...prev, "Got your Account ID ..."]);
                        // Then prompt the AI again to get the response
                        const _aiResponse = await promptSession.prompt(
                            `this is the response : ${JSON.stringify(functionResult)}`
                        );

                        console.log("_aiResponse after sending result to AI : ", _aiResponse);
                        setAiMessages(prev => [...prev, _aiResponse]);
                        setLoading(false);
                        setLoadingStatus('API call complete.');
                    }
                } else if (fnName === "getAccountDetails" ) {
                    const functionResult = await aiFunctions[fnName](param);
                    console.log("functionResult : ", functionResult);
                    if (functionResult) {
                        console.log("sending the result back to AI");
                        setLoading(true);
                        setAiMessages(prev => [...prev, "Got your Account details ..."]);
                        // Then prompt the AI again to get the response
                        const _aiResponse = await promptSession.prompt(
                            `this is the response in json format so i need you to 
                        study it all (key/value pairs) and be able to tell me explicit details 
                        about this data, and note, 
                         don't ask me to paste the JSON response bc I'm including the 
                         JSON data right here : ${JSON.stringify(functionResult)}`
                        );


                        console.log("_aiResponse after sending result to AI : ", _aiResponse);
                        setAiMessages(prev => [...prev, _aiResponse]);
                        setLoading(false);
                        setLoadingStatus('API call complete.');
                    }
                } else if (fnName === "sendMeATestEmail" ) {
                    const functionResult = await aiFunctions[fnName](param);
                    console.log("functionResult : ", functionResult);
                    if (functionResult) {
                        console.log("sending the result back to AI");
                        setLoading(true);
                        setAiMessages(prev => [...prev, "Got your Account details ..."]);
                        // Then prompt the AI again to get the response
                        const _aiResponse = await promptSession.prompt(
                            `ok, we have sent the request tot the server for processing and they 
                            should receive the email momentarily`
                        );

                        console.log("_aiResponse after sending result to AI : ", _aiResponse);
                        setAiMessages(prev => [...prev, _aiResponse]);
                        setLoading(false);
                        setLoadingStatus('API call complete.');
                    }
                } else if (fnName === "getEventPlanningId" ) {
                    const functionResult = await aiFunctions[fnName](param);
                    console.log("functionResult : ", functionResult);
                    if (functionResult) {
                        console.log("sending the result back to AI");
                        setLoading(true);
                        setAiMessages(prev => [...prev, "Got the ID of the event in the Planner..."]);
                        // Then prompt the AI again to get the response
                        const _aiResponse = await promptSession.prompt(
                            `this is the response : ${JSON.stringify(functionResult)}`
                        );

                        console.log("_aiResponse after sending result to AI : ", _aiResponse);
                        setAiMessages(prev => [...prev, _aiResponse]);
                        setLoading(false);
                        setLoadingStatus('API call complete.');
                    }
                } else if (fnName === "getEventSummaryList" || fnName === "getAllEventsSummaryList" ){
                    const functionResult = await aiFunctions[fnName](param);
                    console.log("functionResult : ", functionResult);
                    if (functionResult) {
                        console.log("sending the result back to AI");
                        setLoading(true);
                        setAiMessages(prev => [...prev, "Reviewing your list of Events now ..."]);
                        // Then prompt the AI again to get the response
                        const _aiResponse = await promptSession.prompt(
                            `this is the response in json format so i need you to 
                        study it all (key/value pairs) and be able to tell me explicit details 
                        about this data, and note, 
                         don't ask me to paste the JSON response bc I'm including the 
                         JSON data right here : ${JSON.stringify(functionResult)}`
                        );

                        console.log("_aiResponse after sending result to AI : ", _aiResponse);
                        setAiMessages(prev => [...prev, _aiResponse]);
                        setLoading(false);
                        setLoadingStatus('API call complete.');
                    }
                } else {
                    //don't proceed without an event_id as the parameter
                    if (param !== "event_id"){
                        const functionResult = await aiFunctions[fnName](param);
                        console.log("functionResult : ", functionResult);
                        if (functionResult) {
                            console.log("sending the result back to AI");
                            setLoading(true);
                            setAiMessages(prev => [...prev, "Reviewing the Event data now ..."]);
                            // Then prompt the AI again to get the response
                            const _aiResponse = await promptSession.prompt(
                                `this is the response in json format so i need you to 
                        study it all (key/value pairs) and be able to tell me explicit details 
                        about this data, and note, 
                         don't ask me to paste the JSON response bc I'm including the 
                         JSON data right here : ${JSON.stringify(functionResult)}`
                            );

                            console.log("_aiResponse after sending result to AI : ", _aiResponse);
                            setAiMessages(prev => [...prev, _aiResponse]);
                            setLoading(false);
                            setLoadingStatus('API call complete.');
                        }
                    }
                }
            }
        }

        console.log("handleAiFunctionCallCheck: Nothing found");
        return null; // No function call detected
    };




    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Paper elevation={8} sx={{ p: 4, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.85)' }}>
                {/* Header */}
                <Typography variant="h3" align="center" gutterBottom sx={{ color: purple[700], fontWeight: 'bold' }}>
                    AI Magic Suite
                </Typography>

                <Stack direction="row" justifyContent="center" spacing={3} sx={{ mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1">Prompt</Typography>
                        {apiAvailable.prompt ?
                            <i className="icon-check" style={{color: green[500]}}/> :
                            <i className="icon-cross" style={{color: red[500]}}/>}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1">Translate</Typography>
                        {apiAvailable.translate ?
                            <i className="icon-check" style={{color: green[500]}}/> :
                            <i className="icon-cross" style={{color: red[500]}}/>}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1">Rewrite</Typography>
                        {apiAvailable.rewrite ?
                            <i className="icon-check" style={{color: green[500]}}/> :
                            <i className="icon-cross" style={{color: red[500]}} />}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1">Summarizer</Typography>
                        {apiAvailable.summarize ?
                            <i className="icon-check" style={{color: green[500]}}/> :
                            <i className="icon-cross" style={{color: red[500]}}/>}
                    </Stack>
                </Stack>

                {/* Status */}
                <Alert severity={availabilityStatus === 'available' ? 'success' : availabilityStatus === 'downloadable' ? 'warning' : 'error'} sx={{ mb: 3 }}>
                    {loadingStatus}
                </Alert>
                {!isDesktopDevice && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        Desktop only (Windows, macOS, Linux)
                    </Alert>
                )}

                <Box
                    sx={{
                        display: 'flex'
                    }}
                >
                    {/* Left side: AI messages */}
                    <Box
                        sx={{
                            flex: 1,
                            p: 2,
                            bgcolor: '#f5f5f5',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                            maxHeight: '60vh',
                            borderRight: '1px solid #ddd',
                        }}
                    >
                        <Typography variant="h6" sx={{ color: purple[700], mb: 2 }}>
                            AI Responses
                        </Typography>
                        {aiMessages.map((msg, index) => {
                            const htmlMatch = msg.match(/```html\s*([\s\S]*?)```/i);
                            const jsonMatch = msg.match(/```json\s*([\s\S]*?)```/i);

                            const cleanedText = msg
                                .replace(/```html\s*[\s\S]*?```/gi, '')
                                .replace(/```json\s*[\s\S]*?```/gi, '')
                                .trim();

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        mb: 2,
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: '#e0e0e0',
                                        color: '#000',
                                        maxWidth: '80%',
                                        wordBreak: 'break-word',
                                        boxShadow: 1,
                                        whiteSpace: 'pre-wrap',
                                    }}
                                >
                                    {htmlMatch && (
                                        <div
                                            dangerouslySetInnerHTML={{ __html: htmlMatch[1] }}
                                            style={{ marginBottom: 8 }}
                                        />
                                    )}

                                    {jsonMatch && JSON.parse(jsonMatch[1]).map((item, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                mb: 1,
                                                p: 2,
                                                borderRadius: 2,
                                                boxShadow: 2,
                                                bgcolor: '#fff8e1',
                                            }}
                                        >
                                            {Object.entries(item).map(([key, value]) => (
                                                <div key={key}><strong>{key}:</strong> {value}</div>
                                            ))}
                                        </Box>
                                    ))}

                                    {cleanedText && <span>{cleanedText}</span>}
                                </Box>
                            );
                        })}
                        <div ref={aiEndRef} />
                    </Box>

                    {/* Right side: User chat + input */}
                    <Box
                        sx={{
                            width: 250,
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            maxHeight: '60vh', // Sets the strict limit for the *entire* right column block
                        }}
                    >
                        <Typography variant="h6" sx={{ color: purple[700], mb: 1 }}>
                            Your Messages
                        </Typography>

                        {/* FIX: User chat history gets the flex-grow and overflow scrolling */}
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                pr: 0.5, // slightly buffer scrollbar overlaps
                                mb: 2,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {userMessages.map((msg, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        alignSelf: 'flex-end',
                                        mb: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: purple[500],
                                        color: '#fff',
                                        maxWidth: '80%',
                                        wordBreak: 'break-word',
                                        boxShadow: 1,
                                    }}
                                >
                                    {msg}
                                </Box>
                            ))}
                            <div ref={userEndRef} />
                        </Box>

                        {/* Sticky controls (always locked at bottom of the 60vh container) */}
                        <Box sx={{ flexShrink: 0 }}>
                            <TextField
                                label="Enter your text"
                                multiline
                                rows={3}
                                fullWidth
                                variant="outlined"
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                disabled={!isDesktopDevice || !apiAvailable.prompt || loading}
                            />

                            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" mt={2}>
                                {['prompt', 'translate', 'rewrite', 'summarize'].map((type) => (
                                    <Button
                                        key={type}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleApiCall(type)}
                                        disabled={!isDesktopDevice || !apiAvailable[type] || loading}
                                        sx={{ flex: '1 1 45%', px: 1, py: 1 }}
                                    >
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </Button>
                                ))}
                            </Stack>

                            {loading && (
                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" mt={2}>
                                    <CircularProgress size={20} color="secondary" />
                                    <Typography variant="body2">Processing...</Typography>
                                </Stack>
                            )}
                        </Box>
                    </Box>

                </Box>
            </Paper>
        </Container>
    );
};

export default NanoDashboard;
